<script lang="ts">
	import { browser } from "$app/environment";
  import { PUBLIC_STATIC_ASSETS_BASE_URL } from "$env/static/public";
	import classNames from "classnames";
	import { onMount, tick } from "svelte";
	import { writable, type Writable } from "svelte/store";

  export let src: string;

  let isVisible = false; // Initially hide the image

  const format = src.split('.').pop();
  const DPRs = ['1x', '2x', '3x'];
  const devices = ['mobile', 'tablet', 'desktop', '4k'];
  const sources: Writable<{ type: string; media?: string; srcset: string }[]> = writable([]);

  /**
   * Generate an image URL with the correct device and DPR
   */
  const generateSrc = (device: string, DPR: string) => {
    return src
      .replace(PUBLIC_STATIC_ASSETS_BASE_URL, `${PUBLIC_STATIC_ASSETS_BASE_URL}${device}/${DPR}/`)
      .replace(`.${format}`, `@${DPR}.${format}`);
  };

  /**
   * Build `sources` dynamically, skipping missing images
   */
  const updatedSources: { type: string; media?: string; srcset: string }[] = [];
  for (const device of devices) {
    let srcset = '';
    for (const DPR of DPRs) {
      const generatedSrc = generateSrc(device, DPR);
      if (generatedSrc) {
        srcset += `${generatedSrc} ${DPR}, `;
      }
    }
    if (srcset) {
      updatedSources.push({
        type: `image/${format}`,
        media: device === 'mobile' ? `(max-width: 767px)` :
                device === 'tablet' ? `(min-width: 768px) and (max-width: 1023px)` :
                device === 'desktop' ? `(min-width: 1024px) and (max-width: 1439px)` :
                `(min-width: 1440px)`,
        srcset: srcset.slice(0, -2) // Remove trailing comma and space
      });
    }
  }

  sources.set(updatedSources);

  let imgEl: HTMLImageElement;

  const handleOnError = async () => {
    if (imgEl instanceof HTMLImageElement) {
      imgEl.onerror = null; // Prevent infinite loop
      await tick();
      Array.from(imgEl.parentNode.children).forEach(element => {
        if ((<HTMLImageElement>element).srcset != src) {
        (<HTMLImageElement>element).srcset = src;
        }
      })
      // change the src only if different from current
      if (src != imgEl.src) {
        imgEl.src = src;
      }
      isVisible = true;
    }
  }

  const handleOnLoad = () => {
    isVisible = true;
  };

  onMount(async () => {
    if (imgEl.complete && imgEl.naturalWidth === 0) {
      await handleOnError();
    } else if (imgEl.complete) {
      handleOnLoad();
    }
  });
</script>

{#if $sources.length > 0}
  <picture class={classNames($$restProps.class, { 'hidden': !isVisible })}>
    {#each $sources as source}
      <source type={source.type} media={source.media} srcset={source.srcset} />
    {/each}
    <img
      bind:this={imgEl}
      class={$$restProps.class}
      src={src}
      alt={$$restProps.alt}
      on:load={handleOnLoad}
      on:error={handleOnError}
    />
  </picture>
{/if}